import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { WrenchIcon, CopyIcon, CheckIcon } from './Icons';
import { SAAS_INTEGRATIONS } from '../data/content';

const IntegrationsSection = () => {
  const [copied, setCopied] = useState(false);

  const sampleApiCall = `// Trigger Multi-Agent Workflow Execution
POST /api/orchestrator/plan
Host: api.agentexpert.in
Content-Type: application/json
X-Tenant-ID: tenant_enterprise_99

{
  "workflowId": "wf-auto-refund-882",
  "userRequest": "Cancel subscription and notify via Gmail",
  "kbs": ["RefundPolicy", "SLA_Guidelines"],
  "tools": ["stripe.refund", "gmail.send_notification"],
  "agents": ["RAG_AGENT", "TOOL_AGENT", "CRITIC_AGENT"]
}`;

  const copyCode = () => {
    navigator.clipboard.writeText(sampleApiCall);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="integrations" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-inter">
            <WrenchIcon className="w-3.5 h-3.5" />
            <span>Extensible Tool Registry</span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-slate-900 dark:text-white tracking-tight font-outfit">
            Connect Any Enterprise Tool or SaaS API
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg font-light leading-relaxed">
            From native Google Workspace & Jira bindings to one-click Postman and OpenAPI schema onboarding, Supreme Adventure gives agents the hands to take real action.
          </p>
        </div>

        {/* 2-Column Layout: Left Tools Grid, Right API Code Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Tools Grid */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SAAS_INTEGRATIONS.map((tool, index) => (
              <motion.div
                key={tool.name}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="p-4 rounded-2xl glass-panel hover:border-brand-500/40 hover:shadow-md transition-all duration-200 text-left flex items-start gap-3.5 group"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 flex items-center justify-center text-xl shrink-0 group-hover:scale-105 transition-transform duration-200">
                  {tool.icon}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white font-outfit truncate">
                      {tool.name}
                    </h4>
                    <span className="text-[9px] font-mono font-medium px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                      {tool.category}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 font-light mt-1 leading-snug">
                    {tool.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Right API Code Preview */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl glass-panel p-5 border border-slate-200 dark:border-slate-800 shadow-xl text-left font-mono">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3 mb-3">
                <span className="text-xs font-bold text-brand-600 dark:text-brand-400">
                  REST Execution Endpoint
                </span>
                <button
                  onClick={copyCode}
                  className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white px-2.5 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  {copied ? (
                    <>
                      <CheckIcon className="w-3.5 h-3.5 text-emerald-500" />
                      <span className="text-emerald-500 font-semibold">Copied!</span>
                    </>
                  ) : (
                    <>
                      <CopyIcon className="w-3.5 h-3.5" />
                      <span>Copy</span>
                    </>
                  )}
                </button>
              </div>

              <pre className="text-xs text-slate-800 dark:text-slate-200 overflow-x-auto p-2 bg-slate-50 dark:bg-slate-950/80 rounded-xl leading-relaxed">
                <code>{sampleApiCall}</code>
              </pre>

              <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-500">
                <span>Response: Streaming DAG Tasks</span>
                <span className="text-emerald-500 font-bold">200 OK</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationsSection;
